import { extractUkPostcode } from './chichesterWeather';
import {
  installDateOverrideFrom,
  installEndOverrideFrom,
  installDisplayDay,
  jobCardInstallSpan,
  londonDayKey,
  NO_INSTALL_DATE_KEY,
  parseStamp,
  shiftDayKey,
  toInstallTimeOnly,
} from './jobCardInstallDate';

export { londonDayKey, NO_INSTALL_DATE_KEY, parseStamp };

export type AssignedJob = {
  job_card_id: string;
  job_title: string;
  project_id: string;
  assigned_at: string;
  project_name: string | null;
  customer_name: string | null;
  /** Day 1 of this card's on-site span. */
  install_date: string | null;
  /** Every on-site day for this card; more than one for multi-day installs. */
  install_days?: string[];
  /** Finish time on the last on-site day (HH:MM), when the card sets one. */
  install_end_time?: string | null;
  /** Every day on the calendar invite, which can be longer than this card's days after a crew swap. */
  job_days?: string[];
  supply_only?: boolean | null;
  site_address?: string | null;
  postcode?: string | null;
};

export type InstallEventRow = {
  project_id: string;
  event_date: string | null;
  end_date?: string | null;
  metadata?: unknown;
  status: string | null;
  created_at: string | null;
};

export type CompletionRow = {
  job_card_id: string;
  action: 'completed' | 'released' | 'ended';
  attested_at: string;
};

/** Day each closed card was closed (marked complete or ended and continued on a later card); a later release reopens it. */
export function closedDayByCard(rows: CompletionRow[]): Map<string, string> {
  const latest = new Map<string, CompletionRow>();
  rows.forEach((row) => {
    const current = latest.get(row.job_card_id);
    if (!current || Date.parse(row.attested_at) >= Date.parse(current.attested_at)) latest.set(row.job_card_id, row);
  });
  const closed = new Map<string, string>();
  latest.forEach((row, cardId) => {
    if (row.action === 'released') return;
    const day = londonDayKey(row.attested_at);
    if (day !== NO_INSTALL_DATE_KEY) closed.set(cardId, day);
  });
  return closed;
}

export function jobInstallDays(job: Pick<AssignedJob, 'install_date' | 'install_days'>): string[] {
  if (job.install_days?.length) return job.install_days;
  const key = londonDayKey(job.install_date);
  return key === NO_INSTALL_DATE_KEY ? [] : [key];
}

export function formatGroupTitle(key: string, sampleDate?: string | null, now = new Date()) {
  if (key === NO_INSTALL_DATE_KEY) return 'No install date';
  const date = sampleDate ? parseStamp(sampleDate) : new Date(`${key}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return 'No install date';
  const formatted = date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London',
  });
  const todayKey = londonDayKey(now.toISOString());
  const [year, month, day] = todayKey.split('-').map(Number);
  const tomorrowKey = londonDayKey(new Date(Date.UTC(year, month - 1, day + 1, 12)).toISOString());
  if (key === todayKey) return `Today · ${formatted}`;
  if (key === tomorrowKey) return `Tomorrow · ${formatted}`;
  return formatted;
}

export function latestInstallDatesByProject(events: InstallEventRow[]): Map<string, string> {
  const dates = new Map<string, string>();
  latestInstallEventsByProject(events).forEach((event, projectId) => {
    if (event.event_date) dates.set(projectId, event.event_date);
  });
  return dates;
}

export function latestInstallEventsByProject(events: InstallEventRow[]): Map<string, InstallEventRow> {
  const latest = new Map<string, InstallEventRow>();
  events.forEach((event) => {
    if (event.status === 'cancelled') return;
    const current = latest.get(event.project_id);
    const rowDated = event.event_date ? 1 : 0;
    const currentDated = current?.event_date ? 1 : 0;
    if (!current || rowDated > currentDated) {
      latest.set(event.project_id, event);
      return;
    }
    if (rowDated === currentDated) {
      const rowMs = Date.parse(event.event_date || event.created_at || '') || 0;
      const currentMs = Date.parse(current.event_date || current.created_at || '') || 0;
      if (rowMs > currentMs) latest.set(event.project_id, event);
    }
  });
  latest.forEach((event, projectId) => {
    if (!event.event_date) latest.delete(projectId);
  });
  return latest;
}

export function assembleAssignedJobs(input: {
  assigneeRows: Array<{ job_card_id: string; project_id: string; assigned_at: string }>;
  cards: Array<{
    id: string;
    title: string | null;
    install_date_override?: string | null;
    install_end_date_override?: string | null;
    follows_booking?: boolean | null;
  }>;
  projects: Array<{
    id: string;
    project_name: string | null;
    customer_name: string | null;
    install_date: string | null;
    supply_only?: boolean | null;
    metadata?: unknown;
    site_address?: string | null;
  }>;
  events: InstallEventRow[];
  /** Completion history for these cards; the latest row per card decides whether it is closed. */
  completions?: CompletionRow[];
}): AssignedJob[] {
  const seen = new Set<string>();
  const unique = input.assigneeRows.filter((row) => {
    if (seen.has(row.job_card_id)) return false;
    seen.add(row.job_card_id);
    return true;
  });
  const cardMap = new Map(input.cards.map((card) => [card.id, card]));
  const projectMap = new Map(input.projects.map((project) => [project.id, project]));
  const eventByProject = latestInstallEventsByProject(input.events);
  const closedDay = closedDayByCard(input.completions || []);
  return unique.map((row) => {
    const project = projectMap.get(row.project_id);
    const card = cardMap.get(row.job_card_id);
    const override = installDateOverrideFrom(
      { id: row.job_card_id, install_date_override: card?.install_date_override ?? null },
      { metadata: project?.metadata },
    );
    const calendar = eventByProject.get(row.project_id) || null;
    const fallbackStart = project?.install_date || null;
    const span = jobCardInstallSpan({
      calendar,
      fallbackStart,
      overrideStart: override,
      overrideEnd: installEndOverrideFrom(
        { id: row.job_card_id, install_end_date_override: card?.install_end_date_override ?? null },
        { metadata: project?.metadata },
      ),
      followsBooking: card?.follows_booking !== false,
    });
    // A closed card's crew aren't due back: drop booked days after it was closed.
    const closedOn = closedDay.get(row.job_card_id);
    let days = span.days;
    if (closedOn && days.some((day) => day > closedOn)) {
      const kept = days.filter((day) => day <= closedOn);
      days = kept.length ? kept : [closedOn];
    }
    return {
      job_card_id: row.job_card_id,
      job_title: card?.title || 'Job card',
      project_id: row.project_id,
      assigned_at: row.assigned_at,
      project_name: project?.project_name || null,
      customer_name: project?.customer_name || null,
      install_date: span.start,
      install_days: days,
      install_end_time: days === span.days ? span.endTime || null : null,
      job_days: jobCardInstallSpan({ calendar, fallbackStart }).days,
      supply_only: project?.supply_only ?? null,
      site_address: project?.site_address || null,
      postcode: extractUkPostcode(project?.site_address),
    };
  });
}

export const RECENT_JOBS_KEY = 'recent';
/** Finished jobs stay under Recent for this many days after their last on-site day. */
export const RECENT_JOBS_DAYS = 7;

export function groupJobsByInstallDate(jobs: AssignedJob[], now = new Date()) {
  const buckets = new Map<string, AssignedJob[]>();
  const todayKey = londonDayKey(now.toISOString());
  const recentFrom = shiftDayKey(todayKey, -RECENT_JOBS_DAYS);
  const lastDay = (job: AssignedJob) => jobInstallDays(job).slice(-1)[0] || '';
  jobs.forEach((job) => {
    const days = jobInstallDays(job);
    const last = lastDay(job);
    if (last && last < recentFrom) return;
    const key = last && last < todayKey ? RECENT_JOBS_KEY : installDisplayDay(days, todayKey);
    // A multi-day job is listed under every day still to come, so it shows on each day the crew is on site.
    const keys = key === RECENT_JOBS_KEY || key === NO_INSTALL_DATE_KEY
      ? [key]
      : days.filter((day) => day >= key);
    keys.forEach((dayKey) => {
      const list = buckets.get(dayKey) || [];
      list.push(job);
      buckets.set(dayKey, list);
    });
  });
  const rank = (key: string) => (key === RECENT_JOBS_KEY ? 2 : key === NO_INSTALL_DATE_KEY ? 1 : 0);
  const keys = Array.from(buckets.keys()).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  const byName = (a: AssignedJob, b: AssignedJob) => (
    (a.customer_name || a.project_name || '').localeCompare(b.customer_name || b.project_name || '')
  );
  // When the crew is on site that day: carried-over days from the start of the day, then
  // start times, then jobs with no time set.
  const startOn = (job: AssignedJob, dayKey: string) => {
    if (jobInstallDays(job)[0] !== dayKey) return '00:00';
    return toInstallTimeOnly(job.install_date) || '99:99';
  };
  return keys.map((key) => {
    const items = (buckets.get(key) || []).slice().sort((a, b) => {
      if (key === RECENT_JOBS_KEY) return lastDay(b).localeCompare(lastDay(a)) || byName(a, b);
      return startOn(a, key).localeCompare(startOn(b, key)) || byName(a, b);
    });
    return {
      key,
      title: key === RECENT_JOBS_KEY ? 'Recent' : formatGroupTitle(key, null, now),
      items,
    };
  });
}
